import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/api";

const authHeader = () => ({ "X-Access-Token": localStorage.getItem("guardia_bp_token") || "" });
const blobCache = new Map();

async function fetchClip(text, coach) {
  if (!localStorage.getItem("guardia_bp_token")) throw new Error("no token");
  const key = `${coach}|${text}`;
  if (blobCache.has(key)) return blobCache.get(key);
  const p = api
    .post("/blueprint/voice", { text, coach }, { headers: authHeader(), responseType: "blob" })
    .then((r) => URL.createObjectURL(r.data))
    .catch((e) => { blobCache.delete(key); throw e; });
  blobCache.set(key, p);
  return p;
}

const L = {
  it: {
    warmup: (n, c) => `Si comincia, ragazzo. ${n}. Prima cosa: ${c}`,
    round: (n, c) => `Forza! ${n}! Si parte con: ${c} Mani alte, mento basso. Vai!`,
    rest: () => "Tempo! Torna all'angolo. Respira dal naso, non stai morendo.",
    drill: (n, c) => `${n}. Si parte con: ${c} Pulito, niente scorciatoie!`,
    finisher: (n, c) => `Ultimo sforzo! ${n}! ${c} Svuota il serbatoio!`,
    cooldown: (n, c) => `Defaticamento. Hai lavorato, adesso: ${c}`,
    now: [(c) => `Adesso: ${c}!`, (c) => `Cambia! ${c}! Dai!`, (c) => `Ora ${c}, forza!`],
    last30: "Ultimi trenta secondi! Adesso spingi di più, voglio vederti svuotare il serbatoio!",
    ten: "Dieci secondi! Tutto quello che hai!",
    restEnd: "Dieci secondi, in piedi! Si riparte.",
    count: ["Uno!", "Due!", "Tre!"],
    done: "Ottimo lavoro, campione. Per oggi basta. Domani si ricomincia.",
    hype: [
      "Così! Così mi piace!", "Bello quel colpo, ancora!", "Non mollare adesso, ragazzo!", "Il dolore passa, la gloria resta!",
      "Tu sei un pugile, comportati da pugile!", "Gira l'anca! La potenza viene da terra!", "Respira mentre colpisci!",
      "Mi sembri mia nonna! E lei ha ottantasei anni!", "Quelle mani sono basse, vuoi farti spegnere la luce?",
      "Muovi le gambe, non sei un lampione!", "Ehi principessa, il sacco non ti morde!",
      "Più veloce! Una lumaca col mal di schiena farebbe meglio!", "Mento giù! Il mento in aria lo tengono i turisti!",
      "Ti ho visto più sveglio la domenica mattina!",
    ],
  },
  en: {
    warmup: (n, c) => `Here we go, kid. ${n}. First thing: ${c}`,
    round: (n, c) => `Come on! ${n}! Start with: ${c} Hands up, chin down. Go!`,
    rest: () => "Time! Back to the corner. Breathe through your nose, you ain't dying.",
    drill: (n, c) => `${n}. Start with: ${c} Clean, no shortcuts!`,
    finisher: (n, c) => `Last push! ${n}! ${c} Empty the tank!`,
    cooldown: (n, c) => `Cool-down. You worked, now: ${c}`,
    now: [(c) => `Now: ${c}!`, (c) => `Switch! ${c}! Come on!`, (c) => `Now ${c}, move it!`],
    last30: "Last thirty seconds! Now push harder, I wanna see you empty the tank!",
    ten: "Ten seconds! Everything you got!",
    restEnd: "Ten seconds, on your feet! Here we go again.",
    count: ["One!", "Two!", "Three!"],
    done: "Great work, champ. That's enough for today. Tomorrow we go again.",
    hype: [
      "That's it! That's what I like!", "Nice shot, again!", "Don't you quit on me now, kid!", "Pain goes away, glory stays!",
      "You're a fighter, act like a fighter!", "Turn that hip! Power comes from the ground!", "Breathe when you punch!",
      "You punch like my grandma! And she's eighty-six!", "Those hands are low, you want the lights turned off?",
      "Move your feet, you ain't a lamppost!", "Hey princess, the bag don't bite!",
      "Faster! A snail with a bad back would beat you!", "Chin down! Only tourists keep their chin up!",
      "I've seen you more awake on a Sunday morning!",
    ],
  },
};

const clean = (s) => s.replace(/·/g, ",").replace(/[–—→]/g, ", ").replace(/\s+/g, " ").trim();
const cueText = (cue, lang) => clean(cue[lang] || cue.it);

const CALM = {
  it: {
    warmup: (n, c) => `Iniziamo con calma. ${n}. Primo esercizio: ${c}`,
    round: (n, c) => `${n}. Partiamo con: ${c} Guardia alta, respiro regolare.`,
    rest: () => "Tempo. Recupera, espira lentamente e rilassa le spalle.",
    drill: (n, c) => `${n}. Si comincia con: ${c} Qualità prima della velocità.`,
    finisher: (n, c) => `Ultimo blocco. ${n}. ${c} Mantieni la tecnica anche sotto fatica.`,
    cooldown: (n, c) => `Defaticamento. Ora: ${c}`,
    now: [(c) => `Adesso: ${c}.`, (c) => `Cambiamo. ${c}.`, (c) => `Passiamo a: ${c}.`],
    last30: "Ultimi trenta secondi. Aumenta il ritmo, ma senza perdere la forma.",
    ten: "Dieci secondi. Chiudi pulito.",
    restEnd: "Dieci secondi. Torna in posizione.",
    count: ["Uno.", "Due.", "Tre."],
    done: "Ottimo lavoro. Allenamento completato. Idratati e fai stretching.",
    hype: [
      "Ruota il piede posteriore quando tiri il diretto.", "Il pugno torna in guardia veloce come è partito.",
      "Espira a ogni colpo.", "Mento basso, spalla a proteggere.", "Gomiti stretti, proteggi il fegato.",
      "Resta sulle punte, ginocchia morbide.", "La potenza parte dalle anche, non dal braccio.",
      "Dopo la combinazione, esci d'angolo.", "Rilassa le spalle tra un colpo e l'altro.", "Ottimo ritmo, continua così.",
    ],
  },
  en: {
    warmup: (n, c) => `Let's start easy. ${n}. First exercise: ${c}`,
    round: (n, c) => `${n}. We start with: ${c} Guard up, steady breathing.`,
    rest: () => "Time. Recover, breathe out slowly and relax your shoulders.",
    drill: (n, c) => `${n}. Begin with: ${c} Quality before speed.`,
    finisher: (n, c) => `Final block. ${n}. ${c} Keep your technique under fatigue.`,
    cooldown: (n, c) => `Cool-down. Now: ${c}`,
    now: [(c) => `Now: ${c}.`, (c) => `Switch. ${c}.`, (c) => `Moving to: ${c}.`],
    last30: "Last thirty seconds. Raise the pace, but keep your form.",
    ten: "Ten seconds. Finish clean.",
    restEnd: "Ten seconds. Back in position.",
    count: ["One.", "Two.", "Three."],
    done: "Great work. Workout complete. Hydrate and stretch.",
    hype: [
      "Pivot the rear foot when you throw the cross.", "The fist comes back to guard as fast as it left.",
      "Exhale on every punch.", "Chin down, shoulder up.", "Elbows tight, protect the liver.",
      "Stay on the balls of your feet, soft knees.", "Power starts from the hips, not the arm.",
      "After the combo, take an angle out.", "Relax your shoulders between punches.", "Good rhythm, keep it up.",
    ],
  },
};

export const COACHES = ["gritty", "calm"];
export const coachPhrases = (lang, coach = "gritty") => { const set = coach === "calm" ? CALM : L; return set[lang] || set.it; };

export function blockLine(block, lang, coach) {
  const l = coachPhrases(lang, coach);
  const cue = block.cues?.[0] ? `${cueText(block.cues[0], lang)}.` : "";
  return clean(l[block.kind](clean(block.name[lang] || block.name.it), cue)).slice(0, 220);
}

export const segmentLine = (block, segIdx, lang, coach) => {
  const c = block.cues[segIdx % block.cues.length];
  const t = coachPhrases(lang, coach).now;
  return t[segIdx % t.length](cueText(c, lang)).slice(0, 220);
};

export function allLines(blocks, segmentsOf, lang, coach) {
  const P = coachPhrases(lang, coach);
  const out = [P.last30, P.ten, P.restEnd, P.done, ...P.count, ...P.hype];
  blocks.forEach((b) => {
    out.push(blockLine(b, lang, coach));
    for (let i = 1; i < segmentsOf(b).count; i++) out.push(segmentLine(b, i, lang, coach));
  });
  return out;
}

export function useCoachVoice(lines, onDuck, coach = "gritty") {
  const [enabled, setEnabled] = useState(() => localStorage.getItem("guardia_coach_voice") !== "off");
  const [ready, setReady] = useState(false);
  const audio = useRef(null);
  const speaking = useRef(false);
  const duckRef = useRef(onDuck);
  duckRef.current = onDuck;

  useEffect(() => { localStorage.setItem("guardia_coach_voice", enabled ? "on" : "off"); }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    setReady(false);
    (async () => {
      const queue = [...new Set(lines)];
      const worker = async () => { while (queue.length) { try { await fetchClip(queue.shift(), coach); } catch (e) {} } };
      await Promise.all([worker(), worker(), worker(), worker()]);
      if (alive) setReady(true);
    })();
    return () => { alive = false; };
  }, [enabled, coach, lines.join("|")]); // eslint-disable-line

  const end = () => { speaking.current = false; duckRef.current?.(false); };

  const say = useCallback(async (text) => {
    if (!enabled || !text) return;
    try {
      const url = await fetchClip(text, coach);
      if (audio.current) audio.current.pause();
      const a = new Audio(url);
      audio.current = a;
      a.onended = () => { if (audio.current === a) end(); };
      a.onpause = () => { if (audio.current === a) end(); };
      speaking.current = true;
      duckRef.current?.(true);
      await a.play();
    } catch (e) { end(); }
  }, [enabled, coach]);

  const stop = useCallback(() => { if (audio.current) audio.current.pause(); }, []);

  return { enabled, setEnabled, ready, say, stop, speaking };
}
